import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { GroupModel } from 'src/app/models/archive/group.model';
import { GroupService } from 'src/app/services/archive/group.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-group',
  templateUrl: './register-group.component.html',
  styleUrls: ['./register-group.component.scss'],
  standalone: false
})
export class RegisterGroupComponent implements OnInit {

  disabledForm: boolean = false;
  formGroup: FormGroup = new FormGroup({});
  groupId: number = 0;
  groupContract: GroupModel = new GroupModel();
  permissionUpdatable: boolean = false;
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Grupos';
  projectName = ProjectName.projectName;
  url: string = 'group';

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private groupService: GroupService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(this.groupContract);
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.groupId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `No tiene permisos para eliminar el grupo. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el grupo?')) {
      try {
        await firstValueFrom(this.groupService.deleteGroup(this.groupContract.groupId));
        this.createNewGroup();
        this.toastHandlerService.showToast(
          'success',
          'El grupo fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar el grupo.', error);
      }
    }
  }

  changeGroup(code: string) {
    if (code?.trim() != '') {
      this.getGroupByCode(code);
    } else {
      this.createNewGroup();
    }
  }

  createNewGroup() {
    if (this.permissionUpdatable) {
      this.groupId = 0;
      this.groupContract = new GroupModel();
      this.validateForm(this.groupContract);
      this.updateFormPermision();
      this.location.replaceState(`/${this.url}/register`);
    } else {
      this.updateFormPermision();
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para crear grupos. Comuníquese con el administrador del sistema.`
      );
    }
  }

  async getGroupByCode(code: string) {
    try {
      const response = await firstValueFrom(this.groupService.getGroupByCode(code));
      this.groupId = response.groupId;
      this.groupContract = response;
      this.validateForm(this.groupContract);
      this.location.replaceState(`/${this.url}/register/${this.groupId}`);
    } catch (error) {
      console.error('Error al obtener el grupo por código.', error);
    }
  }

  async getGroupById() {
    try {
      this.groupContract = await firstValueFrom(this.groupService.getGroupById(this.groupId));
      this.validateForm(this.groupContract)
    } catch (error) {
      console.error('Error al obtener la grupo por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionProfileByUrl();
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      this.permissionUpdatable = false;
      console.error('Error al cargar permisos.', error);
    }
  }

  async handleInitialState() {
    if (this.groupId !== null && this.groupId !== 0) {
      this.getGroupById();
    } else {
      this.createNewGroup();
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formGroup.valid) {
      const modelGroup = this.formGroup.value as GroupModel;
      if (this.groupId == 0) {
        try {
          const response = await firstValueFrom(this.groupService.createGroup(modelGroup));
          this.groupId = response.groupId;
          this.groupContract = response;
          this.validateForm(this.groupContract);
          this.location.replaceState(`/${this.url}/register/${this.groupId}`);
          this.toastHandlerService.showToast(
            'success',
            `El grupo fue creado exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear el grupo.', error);
        }
      } else {
        this.groupContract.name = modelGroup.name;
        try {
          const response = await firstValueFrom(this.groupService.updateGroup(this.groupContract));
          this.groupContract = response;
          this.validateForm(this.groupContract);
          this.toastHandlerService.showToast(
            'success',
            `El grupo fue modificado exitosamente.`
          );
        } catch (error) {
          console.error('Error al modificar el grupo.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.',
        maxlength: 'El Código no puede superar el tamaño máximo de 3 caracteres.',
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El Nombre no puede superar el tamaño máximo de 50 caracteres.',
      },
    };

    Object.keys(controlMessages).forEach((controlName) => {
      const formControl = this.formGroup.get(controlName);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[controlName];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formGroup.enable(), 100);
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formGroup.disable(), 100);
    }
  }

  validateForm(group: GroupModel) {
    this.formGroup = this.formBuilder.group({
      code: [group.code, [Validators.required, Validators.maxLength(3)]],
      name: [group.name, [Validators.required, Validators.maxLength(50)]]
    });
  }

  validateFormControls() {
    Object.keys(this.formGroup.controls).forEach((key) => {
      const control = this.formGroup.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
